import axios from "axios";
import {
  getLocalStorageSkedApiToken,
  setLocalStorageSkedApiToken,
  removeLocalStorageSkedApiToken,
  SKED_API_TOKEN_REMOVED_EVENT,
} from "libs/storage/adapters";

const MAX_AUTH_RETRIES = 3;
let refreshPromise = null;
// Local storage is shared by accounts and tabs. Only reuse a token fetched
// from this page's browser session; a new page must resolve /api-jwt first.
let sessionToken = null;
let tokenGeneration = 0;

function invalidateSessionToken() {
  sessionToken = null;
  refreshPromise = null;
  tokenGeneration++;
  // Rejection belongs to the previous session; let new cookies prove themselves.
  sessionInvalid = false;
}

window.addEventListener(SKED_API_TOKEN_REMOVED_EVENT, invalidateSessionToken);

// A removal event can arrive after another tab has already written a new token.
// Remember the invalidation instead of checking the key's current presence.
window.addEventListener("storage", (event) => {
  if (
    event.storageArea === window.localStorage &&
    (event.key === "skedApiToken" || event.key === null) &&
    event.newValue === null
  ) {
    invalidateSessionToken();
  }
});

/**
 * Shared flag — once any client proves the session is irrecoverably dead,
 * all further auth retries are short-circuited so requests fail fast and
 * the UI can show its normal error/empty states instead of spinning.
 */
let sessionInvalid = false;

/**
 * Create a per-instance retry state object. Each axios client should
 * own its own instance so that concurrent 401s from different clients
 * don't prematurely exhaust the shared counter.
 */
export function createAuthRetryState() {
  return { count: 0 };
}

/**
 * Shared token refresh that deduplicates concurrent refresh attempts.
 * Both skedApi and skedInboxApi interceptors should use this instead
 * of independently calling /api-jwt.
 */
export function refreshToken() {
  if (sessionInvalid) {
    return Promise.reject(
      new Error("Session invalid — skipping token refresh")
    );
  }

  if (refreshPromise) {
    return refreshPromise;
  }

  const requestedGeneration = tokenGeneration;
  const promise = axios
    .get("/api-jwt")
    .then((response) => {
      if (requestedGeneration !== tokenGeneration) {
        throw new Error("Authentication context changed");
      }
      const token = response.data.jwt;
      sessionToken = token;
      setLocalStorageSkedApiToken(token);
      return token;
    })
    .finally(() => {
      if (refreshPromise === promise) refreshPromise = null;
    });
  refreshPromise = promise;
  return promise;
}

/**
 * Read the page's established token for synchronous consumers such as media URLs.
 * Storage presence honors explicit invalidation; its value is never adopted.
 */
export function getCachedToken() {
  if (!getLocalStorageSkedApiToken() && sessionToken) invalidateSessionToken();
  return sessionInvalid ? null : sessionToken;
}

/**
 * Get a token established by this page's current browser session.
 */
export async function getToken(forceRequest = false) {
  if (refreshPromise) return refreshPromise;
  const token = getCachedToken();
  if (token && !forceRequest) return token;
  return refreshToken();
}

/**
 * Handle a 401 error by refreshing the token and retrying the request.
 * Returns the retried request promise, or throws if max retries exceeded.
 *
 * @param {import("axios").AxiosInstance} apiInstance - The axios instance to retry with
 * @param {import("axios").AxiosError} error - The original 401 error
 * @param {string} label - Label for logging (e.g. "skedApi", "skedInboxApi")
 * @param {{ count: number }} retryState - Per-instance retry counter
 */
export async function handleAuthError(apiInstance, error, label, retryState) {
  // Once any client has proven the session is dead, fail immediately.
  if (sessionInvalid) {
    throw error;
  }

  if (retryState.count >= MAX_AUTH_RETRIES) {
    // The token was refreshed successfully MAX_AUTH_RETRIES times and this
    // endpoint still 401s. That is an endpoint-specific authorization denial
    // (e.g. sked-inbox's showInbox gate returning 401 for collaborators who
    // can't access the inbox), NOT a dead session. Fail only this request —
    // do NOT invalidate the shared session or delete the still-valid token,
    // or unrelated skedApi calls (post comments, etc.) would break too.
    console.error(
      `[${label}] Max auth retries (${MAX_AUTH_RETRIES}) exceeded; failing request without invalidating session.`
    );
    throw error;
  }

  retryState.count++;
  console.log(
    `[${label}] Invalid token (attempt ${retryState.count}/${MAX_AUTH_RETRIES})`
  );
  const previousToken = getCachedToken();
  const requestedGeneration = tokenGeneration;
  sessionToken = null;

  try {
    await refreshToken();
  } catch (refreshError) {
    if (requestedGeneration !== tokenGeneration) throw error;
    const status = refreshError?.response?.status;
    if (status === 401 || status === 403) {
      // /api-jwt explicitly rejected the session cookie — genuinely dead.
      removeLocalStorageSkedApiToken();
      // Preserve this rejection after the removal event resets the old context.
      sessionInvalid = true;
    } else if (previousToken) {
      // Transient failure (e.g. network): restore the token we cleared so
      // concurrent requests aren't left tokenless. The session is not dead,
      // so don't poison it — the retry counter alone gates further attempts.
      sessionToken = previousToken;
      setLocalStorageSkedApiToken(previousToken);
    }
    throw error;
  }

  if (!error?.config) throw error;
  return apiInstance(error.config);
}

/**
 * Reset the retry counter for a specific instance. Call on any successful response.
 * Also clears the session-invalid flag since a successful response proves the
 * session is alive (e.g. user logged back in and navigated without a full reload).
 */
export function resetAuthRetryCount(retryState) {
  retryState.count = 0;
  sessionInvalid = false;
}

/**
 * Reset the session-invalid flag so auth retries are possible again.
 * Call on client-side navigation so that page changes give auth a fresh chance.
 */
export function resetSessionState() {
  sessionInvalid = false;
}
